import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { CopyStrategyConfigDto } from 'api/copyBotApi';
import { useSaveSettings } from 'hooks/meme/useSaveSettings';
import { store } from 'store';
import { PortfolioResponse } from 'types/portfolio';

import * as S from '../common.styles';

const MIN_COPY_SIZE_USD = 5;

const SettingLabel = ({ label, hint }: { label: string; hint: string }) => (
  <S.SettingsLabelRow>
    <span>{label}</span>
    <S.SettingsTooltip data-tooltip={hint} tabIndex={0} aria-label={hint} />
  </S.SettingsLabelRow>
);

type SettingsFormState = {
  copySize: string;
  minLeaderTradeSizeUSD: string;
  maxOrderSizeUSD: string;
  minOrderSizeUSD: string;
  maxPositionSizeUSD: string;
  maxDailyVolumeUSD: string;
};

const optionalToString = (value?: number) => (value == null ? '' : String(value));

const getFormState = (portfolio?: PortfolioResponse | null): SettingsFormState => ({
  copySize: optionalToString(portfolio?.copySize),
  minLeaderTradeSizeUSD: optionalToString(portfolio?.minLeaderTradeSizeUSD),
  maxOrderSizeUSD: optionalToString(portfolio?.maxOrderSizeUSD),
  minOrderSizeUSD: optionalToString(portfolio?.minOrderSizeUSD),
  maxPositionSizeUSD: optionalToString(portfolio?.maxPositionSizeUSD),
  maxDailyVolumeUSD: optionalToString(portfolio?.maxDailyVolumeUSD),
});

const parseRequiredNumber = (value: string) => {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseOptionalNumber = (value: string) => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const sanitizeStrategyConfig = (config: CopyStrategyConfigDto): CopyStrategyConfigDto => ({
  strategy: 'FIXED',
  copySize: config.copySize,
  maxOrderSizeUSD: config.maxOrderSizeUSD,
  minOrderSizeUSD: config.minOrderSizeUSD,
  minLeaderTradeSizeUSD: config.minLeaderTradeSizeUSD,
  maxPositionSizeUSD: config.maxPositionSizeUSD,
  maxDailyVolumeUSD: config.maxDailyVolumeUSD,
});

const getPortfolioStrategyConfig = (portfolio: PortfolioResponse): CopyStrategyConfigDto =>
  sanitizeStrategyConfig({
    strategy: 'FIXED',
    copySize: portfolio.copySize,
    minLeaderTradeSizeUSD: portfolio.minLeaderTradeSizeUSD,
    maxOrderSizeUSD: portfolio.maxOrderSizeUSD,
    minOrderSizeUSD: portfolio.minOrderSizeUSD,
    maxPositionSizeUSD: portfolio.maxPositionSizeUSD,
    maxDailyVolumeUSD: portfolio.maxDailyVolumeUSD,
  });

const buildPayload = (form: SettingsFormState) => {
  const copySize = parseRequiredNumber(form.copySize);
  const maxOrderSizeUSD = parseRequiredNumber(form.maxOrderSizeUSD);
  const minOrderSizeUSD = parseRequiredNumber(form.minOrderSizeUSD);
  const minLeaderTradeSizeUSD = parseOptionalNumber(form.minLeaderTradeSizeUSD);
  const maxPositionSizeUSD = parseOptionalNumber(form.maxPositionSizeUSD);
  const maxDailyVolumeUSD = parseOptionalNumber(form.maxDailyVolumeUSD);

  const isCopySizeInvalid = copySize == null || copySize < MIN_COPY_SIZE_USD;
  const isMaxOrderInvalid = maxOrderSizeUSD == null || maxOrderSizeUSD <= 0;
  const isMinOrderInvalid = minOrderSizeUSD == null || minOrderSizeUSD <= 0;
  const isMinLeaderTradeInvalid =
    minLeaderTradeSizeUSD === null || (minLeaderTradeSizeUSD != null && minLeaderTradeSizeUSD < 0);
  const isMaxPositionInvalid =
    maxPositionSizeUSD === null || (maxPositionSizeUSD != null && maxPositionSizeUSD <= 0);
  const isMaxDailyVolumeInvalid =
    maxDailyVolumeUSD === null || (maxDailyVolumeUSD != null && maxDailyVolumeUSD <= 0);

  const payload =
    isCopySizeInvalid ||
    isMaxOrderInvalid ||
    isMinOrderInvalid ||
    isMinLeaderTradeInvalid ||
    isMaxPositionInvalid ||
    isMaxDailyVolumeInvalid
      ? null
      : sanitizeStrategyConfig({
          strategy: 'FIXED',
          copySize,
          minLeaderTradeSizeUSD,
          maxOrderSizeUSD,
          minOrderSizeUSD,
          maxPositionSizeUSD,
          maxDailyVolumeUSD,
        });

  return {
    payload,
    errors: {
      copySize: isCopySizeInvalid,
      maxOrderSizeUSD: isMaxOrderInvalid,
      minOrderSizeUSD: isMinOrderInvalid,
      minLeaderTradeSizeUSD: isMinLeaderTradeInvalid,
      maxPositionSizeUSD: isMaxPositionInvalid,
      maxDailyVolumeUSD: isMaxDailyVolumeInvalid,
    },
  };
};

export const Settings = observer(() => {
  const { account } = store.web3Store;
  const { portfolio, setTab } = store.portfolioStore;

  const saveSettings = useSaveSettings();

  const [form, setForm] = useState<SettingsFormState>(getFormState(portfolio));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setForm(getFormState(portfolio));
  }, [portfolio]);

  const { payload, errors } = useMemo(() => buildPayload(form), [form]);

  const isDirty = useMemo(() => {
    if (!portfolio || !payload) return false;
    return JSON.stringify(payload) !== JSON.stringify(getPortfolioStrategyConfig(portfolio));
  }, [payload, portfolio]);

  const hasErrors = Object.values(errors).some(Boolean);

  const setField =
    (field: keyof SettingsFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleReset = () => {
    setForm(getFormState(portfolio));
  };

  const handleCancel = () => {
    handleReset();
    setTab('wallet');
  };

  const handleSave = async () => {
    if (!payload) return;

    setIsSaving(true);
    await saveSettings(payload);
    setIsSaving(false);
  };

  return (
    <S.SettingsContent>
      <S.SettingsGrid>
        <S.SettingsLabel>
          <SettingLabel
            label="Copy size (USD)"
            hint={`Fixed strategy uses this USD amount for every copied buy. Minimum copy size is $${MIN_COPY_SIZE_USD}.`}
          />
          <S.SettingsInput
            type="number"
            min={MIN_COPY_SIZE_USD}
            step="any"
            value={form.copySize}
            onChange={setField('copySize')}
            $isError={errors.copySize}
          />
          {errors.copySize && <S.SettingsHint $isError>Minimum copy size is ${MIN_COPY_SIZE_USD}.</S.SettingsHint>}
        </S.SettingsLabel>

        <S.SettingsLabel>
          <SettingLabel
            label="Min leader trade size (USD)"
            hint="Skip leader buys smaller than this amount before any copy order is calculated."
          />
          <S.SettingsInput
            type="number"
            min={0}
            step="any"
            value={form.minLeaderTradeSizeUSD}
            onChange={setField('minLeaderTradeSizeUSD')}
            $isError={errors.minLeaderTradeSizeUSD}
          />
        </S.SettingsLabel>
      </S.SettingsGrid>

      <S.SettingsSectionTitle>Safety limits</S.SettingsSectionTitle>
      <S.SettingsGrid>
        <S.SettingsLabel>
          <SettingLabel
            label="Max order size (USD)"
            hint="Hard cap for one copied trade after the fixed copy size is applied."
          />
          <S.SettingsInput
            type="number"
            min={0}
            step="any"
            value={form.maxOrderSizeUSD}
            onChange={setField('maxOrderSizeUSD')}
            $isError={errors.maxOrderSizeUSD}
          />
        </S.SettingsLabel>

        <S.SettingsLabel>
          <SettingLabel
            label="Min order size (USD)"
            hint="If the final copied order is below this threshold, the trade is skipped."
          />
          <S.SettingsInput
            type="number"
            min={0}
            step="any"
            value={form.minOrderSizeUSD}
            onChange={setField('minOrderSizeUSD')}
            $isError={errors.minOrderSizeUSD}
          />
        </S.SettingsLabel>

        <S.SettingsLabel>
          <SettingLabel
            label="Max position size (USD)"
            hint="Optional cap for the total size of one open position for this wallet."
          />
          <S.SettingsInput
            type="number"
            min={0}
            step="any"
            value={form.maxPositionSizeUSD}
            onChange={setField('maxPositionSizeUSD')}
            $isError={errors.maxPositionSizeUSD}
          />
        </S.SettingsLabel>

        <S.SettingsLabel>
          <SettingLabel
            label="Max daily volume (USD)"
            hint="Optional daily limit for total copied volume. Trades above the cap are skipped."
          />
          <S.SettingsInput
            type="number"
            min={0}
            step="any"
            value={form.maxDailyVolumeUSD}
            onChange={setField('maxDailyVolumeUSD')}
            $isError={errors.maxDailyVolumeUSD}
          />
        </S.SettingsLabel>
      </S.SettingsGrid>

      <S.SettingsButtons>
        <S.ResetButton onClick={handleReset} disabled={!portfolio || !isDirty}>
          Reset Changes
        </S.ResetButton>
        {(() => {
          if (!account) {
            return (
              <S.SettingsBlueButton isFullWidth disabled>
                Connect Wallet
              </S.SettingsBlueButton>
            );
          }

          if (isSaving) {
            return (
              <S.SettingsBlueButton isFullWidth disabled>
                Submitting
              </S.SettingsBlueButton>
            );
          }

          if (!payload) {
            return (
              <S.SettingsBlueButton isFullWidth disabled>
                Fix Invalid Fields
              </S.SettingsBlueButton>
            );
          }

          if (!isDirty) {
            return (
              <S.SettingsBlueButton isFullWidth onClick={handleCancel}>
                Cancel
              </S.SettingsBlueButton>
            );
          }

          return (
            <S.SettingsBlueButton isFullWidth onClick={handleSave} disabled={hasErrors}>
              Save Settings
            </S.SettingsBlueButton>
          );
        })()}
      </S.SettingsButtons>
    </S.SettingsContent>
  );
});
